/**
 * <hidden-evey-mount> — relocates Evey's date/time trigger into the rail's
 * "Select Date and Time" slot.
 *
 * Evey injects its trigger button inside the buy-buttons form, near the bottom of
 * the rail. The frame puts it directly under the "Select Date and Time" heading,
 * above the ticket rows. Liquid can't place it (Evey renders client-side) and CSS
 * order can't either (it's a descendant of buy-buttons, not a sibling of the slot),
 * so the node is moved once it appears.
 *
 * Moving rather than cloning is deliberate: Evey keeps its own listeners on that
 * element, and a clone would open nothing.
 *
 * Finding it: an explicit CSS selector wins if one is configured. Otherwise we match
 * the button's visible label, which is what we can actually observe without guessing
 * at Evey's internal class names. Both are block settings, so this is retargetable
 * without a code change when Evey's markup shifts.
 *
 * Markup contract:
 *   <hidden-evey-mount data-trigger-selector="" data-trigger-text="date/time">
 *     <div data-evey-slot="datetime"></div>
 *     <div data-evey-slot="tickets"> … buy-buttons … </div>
 *   </hidden-evey-mount>
 */
/**
 * A clock time followed by a second one: "11 AM - 11:30 AM", "1:15 PM – 3 PM".
 * Deliberately narrow — the strings this runs over also contain hyphens that are not
 * ranges ("Ticket #1 - Weekend / Saturday, ..."), so both sides must look like a time
 * before anything is cut. Capture 1 is the start time, which is all the frame shows.
 */
const TIME_RANGE = /(\d{1,2}(?::\d{2})?\s*[ap]\.?m\.?)\s*[-\u2013\u2014]\s*\d{1,2}(?::\d{2})?\s*[ap]\.?m\.?/gi;

class HiddenEveyMount extends HTMLElement {
  connectedCallback() {
    this.target = this.querySelector('[data-evey-slot="datetime"]');
    this.source = this.querySelector('[data-evey-slot="tickets"]');
    if (!this.target || !this.source) return;

    // The field is drawn at data-reserved height before Evey's button exists, so it
    // looks ready while a click on it has nothing to land on. Remember that click and
    // honour it the moment the trigger arrives, instead of silently dropping it and
    // making the customer click a second time.
    this.onEarlyClick = (event) => {
      if (!event.target.closest('[data-evey-slot="datetime"]')) return;
      const trigger = this.trigger;
      // Present AND usable means Evey's own button took the click; nothing to replay.
      // Relocated but still disabled is the second dead window and must be caught too.
      if (trigger && !trigger.disabled && !trigger.classList.contains('desaturated')) return;
      this.pendingOpenAt = performance.now();
      if (trigger) this.openWhenReady(trigger);
    };
    this.target.addEventListener('click', this.onEarlyClick);

    // Evey may already have rendered before this element upgrades.
    if (this.relocate()) return;

    this.observer = new MutationObserver(() => {
      if (this.relocate()) this.observer.disconnect();
    });
    this.observer.observe(this.source, { childList: true, subtree: true });

    // The modal is appended to <body>, outside this element, and is rebuilt each time
    // a day is picked — so watch the document for it rather than binding once.
    this.slotObserver = new MutationObserver(() => {
      if (this.collapsing) return;
      this.collapsing = true;
      try {
        this.collapseSlots();
        this.shortenTimes();
        this.swapCloseIcon();
        this.lockPageBehind();
      } finally {
        this.collapsing = false;
      }
    });
    // Attributes as well as children: Evey does not mount and unmount its modals, it
    // leaves both in the DOM and flips them between 0x0 and full-screen with a style
    // change. A childList-only observer never saw an open — which is why the page
    // lock below looked dead. Filtered to the three attributes that can carry that.
    this.slotObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class', 'hidden'],
    });

    this.pinAddToCart();

    // Stop watching eventually rather than observing for the life of the page —
    // if Evey hasn't rendered by now it isn't going to.
    this.timer = setTimeout(() => this.observer?.disconnect(), 15000);
  }

  disconnectedCallback() {
    if (this.onEarlyClick) this.target?.removeEventListener('click', this.onEarlyClick);
    this.stopWaitingToOpen();
    this.unblockBackgroundScroll();
    this.observer?.disconnect();
    this.labelObserver?.disconnect();
    this.slotObserver?.disconnect();
    this.pinObserver?.disconnect();
    this.resize?.disconnect();
    clearInterval(this.lockPoll);
    delete document.documentElement.dataset.eveyModalOpen;
    clearTimeout(this.timer);
  }

  /** @returns {boolean} true once the trigger has been moved */
  relocate() {
    const trigger = this.findTrigger();
    if (!trigger) return false;

    this.target.appendChild(trigger);
    this.target.setAttribute('data-evey-trigger', 'relocated');

    this.trigger = trigger;
    this.relabel();
    this.hideOrphanLabel();

    // Evey rewrites this button's text — back to its placeholder when the selection
    // is cleared, and to the chosen date when one is picked. Watch it so our wording
    // survives, without touching a real date once one is set.
    this.labelObserver = new MutationObserver(() => {
      this.relabel();
      // Evey rewrites this label with the full range each time a slot is picked, and
      // that write fires no body mutation — so the trim has to run from here as well.
      this.shortenTimes();
    });
    this.labelObserver.observe(trigger, { childList: true, subtree: true, characterData: true });

    if (this.pendingOpenAt !== undefined) this.openWhenReady(trigger);

    return true;
  }

  /**
   * Replay a click the customer made before the picker could take one.
   *
   * There are two dead windows on a cold load, both measured on this store: the field
   * is drawn with no button in it at all for ~3.3s, and then Evey's button sits in it
   * `disabled` until its schedule has loaded, ~9.3s. A click anywhere in there does
   * nothing, which is what makes the field feel broken and gets it clicked twice.
   *
   * Firing at relocation is not enough — the button is still disabled at that point —
   * so this waits for it to actually become usable.
   *
   * @param {HTMLButtonElement} trigger
   */
  openWhenReady(trigger) {
    // May be called again by a second early click before the first wait resolves.
    this.readyObserver?.disconnect();
    clearTimeout(this.pendingTimer);

    const ready = () => !trigger.disabled && !trigger.classList.contains('desaturated');

    const fire = () => {
      const waited = performance.now() - this.pendingOpenAt;
      this.stopWaitingToOpen();
      // Bounded: a picker that opens by itself long after the customer gave up and
      // started reading something else is worse than the dropped click.
      if (waited < 15000) trigger.click();
    };

    if (ready()) return fire();

    this.readyObserver = new MutationObserver(() => {
      if (ready()) fire();
    });
    this.readyObserver.observe(trigger, { attributes: true, attributeFilter: ['disabled', 'class'] });
    this.pendingTimer = setTimeout(() => this.stopWaitingToOpen(), 15000);
  }

  stopWaitingToOpen() {
    this.pendingOpenAt = undefined;
    this.readyObserver?.disconnect();
    this.readyObserver = undefined;
    clearTimeout(this.pendingTimer);
  }

  /**
   * Swap Evey's "Choose date/time" placeholder for the wording the frame specifies.
   * Only text matching the placeholder is replaced, so a selected date is never
   * overwritten — and the replacement deliberately does not contain the match string,
   * which is what stops this rewriting its own output forever.
   */
  relabel() {
    const label = (this.dataset.triggerPlaceholder || '').trim();
    if (!label || !this.trigger) return;

    const needle = (this.dataset.triggerText || 'date/time').trim().toLowerCase();
    if (!needle) return;

    const walker = document.createTreeWalker(this.trigger, NodeFilter.SHOW_TEXT);
    for (let node = walker.nextNode(); node; node = walker.nextNode()) {
      const text = (node.nodeValue || '').trim();
      if (!text || !text.toLowerCase().includes(needle)) continue;
      if (text === label) continue;
      node.nodeValue = label;
    }
  }

  /**
   * Collapse Evey's duplicate time-slot rows.
   *
   * Evey renders one row per TICKET TYPE, not per event: a single 11:00 slot offering
   * Weekday + Student produces two identical-looking rows that set the same event.
   * The frame shows one row per slot ("Tue 30 — 11 AM ›"), so all but the first row
   * of each date+time group is hidden and the now-redundant type label removed.
   *
   * ASSUMPTION, and the reason this is a setting rather than unconditional: one event
   * per date+time. There is no event id anywhere in the row markup and Evey exposes no
   * schedule data on window, so date+time text is the only key available. If a date is
   * misconfigured with two DIFFERENT events at the same time, this hides one of them —
   * and which survives depends on Evey's ordering. Turn it off if that ever happens.
   */
  collapseSlots() {
    if (this.dataset.collapseSlots !== 'true') return;

    const list = document.querySelector('#evey-schedule-calendar-modal .available-events');
    if (!list) return;

    const seen = new Set();
    let changed = false;

    for (const row of list.children) {
      if (!(row instanceof HTMLElement)) continue;

      const date = row.querySelector('.schedule-event-left')?.textContent.trim() || '';
      const time = row.querySelector('.schedule-event-time')?.textContent.trim() || '';
      const key = `${date}|${time}`;

      const duplicate = seen.has(key);
      seen.add(key);

      if (row.hidden !== duplicate) {
        row.hidden = duplicate;
        changed = true;
      }

      // With one row per slot the ticket type names only one of the types on offer,
      // which is worse than showing none.
      const type = row.querySelector('.schedule-event-ticket-type');
      if (type && !type.hidden) {
        type.hidden = true;
        changed = true;
      }
    }

    return changed;
  }

  /**
   * "11 AM - 11:30 AM" → "11 AM", wherever Evey prints an event's time.
   *
   * The frame labels a slot with its START time only; the end is implied by the
   * duration already given in the game features. Evey has no setting for this, so
   * the rendered text is trimmed in place — in the slot rows, on the date/time
   * trigger in the rail, and in the attendee form's per-ticket headings.
   *
   * Text nodes are walked rather than textContent rewritten: these elements carry
   * icons and nested spans that a bulk write would destroy.
   *
   * Idempotent by construction — once the second time is gone the pattern cannot
   * match again, so the write this makes cannot feed the observer that called it.
   */
  shortenTimes() {
    const targets = [
      ...document.querySelectorAll('#evey-schedule-calendar-modal .schedule-event-time'),
      ...document.querySelectorAll('#evey-attendee-info-modal .event-ticket-heading'),
      this.trigger,
    ].filter(Boolean);

    for (const target of targets) {
      const walker = document.createTreeWalker(target, NodeFilter.SHOW_TEXT);
      for (let node = walker.nextNode(); node; node = walker.nextNode()) {
        const text = node.nodeValue || '';
        const trimmed = text.replace(TIME_RANGE, '$1');
        if (trimmed !== text) node.nodeValue = trimmed;
      }
    }
  }

  /**
   * Freeze the page behind an open Evey modal.
   *
   * Both Evey overlays are fixed, full-viewport and scroll their own content, but a
   * wheel over them was reaching the document instead — measured on the attendee-info
   * modal, where the element under the cursor hit-tested to the page behind. CSS
   * alone cannot fix that: overscroll-behavior only contains a scroll that actually
   * starts in the overlay. So the document is frozen for as long as a modal is up.
   *
   * Openness is read from the rendered box rather than from a class or a style
   * attribute: Evey leaves #evey-attendee-info-modal in the DOM at 0x0 when closed,
   * and nothing about its markup says which state it is in.
   *
   * The interval is the safety net, not the mechanism. The body observer drives this,
   * but it only watches childList — if Evey ever closes a modal by a means that fires
   * no mutation the poll still clears the lock within half a second. A page frozen
   * forever is the one failure mode that would be worse than the bug.
   */
  lockPageBehind() {
    // An overlay Evey has parked with .visually-hidden is closed whatever its box says:
    // that class is a 1px clip, and a theme rule that bumps its display leaves it
    // measurable. Anything else counts as open only with a real footprint — the
    // attendee-info overlay is left in the DOM at 0x0 when closed.
    const open = [...document.querySelectorAll('.evey-modal')].some(
      (modal) =>
        !modal.classList.contains('visually-hidden') && modal.offsetWidth > 1 && modal.offsetHeight > 1
    );

    const root = document.documentElement;
    if (open === (root.dataset.eveyModalOpen === 'true')) return;

    root.dataset.eveyModalOpen = String(open);

    // overflow:hidden alone is not a lock in this theme: Lenis drives the scroll
    // position from JS and sails straight past it. Same idiom as
    // hidden-ticket-popup.js #setScrollLocked; absent under reduced motion and in
    // the theme editor, hence the optional call.
    const lenis = globalThis.__hiddenLenis;
    if (lenis) open ? lenis.stop() : lenis.start();

    open ? this.blockBackgroundScroll() : this.unblockBackgroundScroll();

    clearInterval(this.lockPoll);
    if (open) this.lockPoll = setInterval(() => this.lockPageBehind(), 500);
  }

  /**
   * Refuse scroll gestures that begin outside the overlay.
   *
   * This used to be overflow:hidden on html and body, which is the usual idiom but
   * makes them non-scrolling containers — and a position:sticky element resolves
   * against its nearest scrolling ancestor, so the media rail stopped being stuck and
   * the banner jumped to its static position the moment the picker opened.
   *
   * Blocking the gestures leaves every containing block alone, so sticky survives.
   * Keys are included because Space and the arrows scroll the document too, and they
   * are only swallowed when focus is outside the overlay so the modal stays keyboard
   * operable.
   */
  blockBackgroundScroll() {
    if (this.scrollBlocker) return;

    this.scrollBlocker = (event) => {
      if (event.target.closest?.('.evey-modal')) return;
      event.preventDefault();
    };
    this.keyBlocker = (event) => {
      const keys = ['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '];
      if (!keys.includes(event.key)) return;
      if (document.activeElement?.closest('.evey-modal')) return;
      event.preventDefault();
    };

    for (const type of ['wheel', 'touchmove']) {
      document.addEventListener(type, this.scrollBlocker, { passive: false, capture: true });
    }
    document.addEventListener('keydown', this.keyBlocker, { passive: false, capture: true });
  }

  unblockBackgroundScroll() {
    if (!this.scrollBlocker) return;
    for (const type of ['wheel', 'touchmove']) {
      document.removeEventListener(type, this.scrollBlocker, { capture: true });
    }
    document.removeEventListener('keydown', this.keyBlocker, { capture: true });
    this.scrollBlocker = undefined;
    this.keyBlocker = undefined;
  }

  /**
   * Pin Add to Cart to the bottom of the product rail.
   *
   * It should hold against the bottom of the viewport while the rail scrolls, then
   * STOP at the rail's end so it never covers the footer — i.e. the bottom of the
   * right column, as the frame draws it.
   *
   * position:sticky can't express that here: the sticky range is bounded by the
   * element's parent, and Evey's <form> ends a few pixels after the button. So the
   * button is fixed while a sentinel at the end of the rail is still below the fold,
   * and released the moment that sentinel scrolls into view — at which point the
   * button sits in flow at the rail's end, above the footer.
   *
   * Fixed positioning takes it out of flow, so the form is padded by the button's
   * height while pinned; without that the rail would shorten and the page jump.
   */
  pinAddToCart() {
    const rail = this.closest('.hidden-pdp__scroll') || this.closest('.hidden-pdp__rail');
    const button = document.querySelector('.evey__multi-variant-selector button[type="submit"]');
    if (!rail || !button) return;

    const form = button.closest('form');

    // Zero-height marker at the very end of the rail: once it is on screen there is
    // no more rail to scroll, so the bar belongs in flow rather than over the footer.
    const sentinel = document.createElement('div');
    sentinel.setAttribute('data-atc-sentinel', '');
    sentinel.style.cssText = 'height:0;width:100%;pointer-events:none;';
    rail.appendChild(sentinel);

    // The bar is out of flow in BOTH states — fixed to the viewport while pinned,
    // absolute against the rail's bottom edge once released — so the space it needs
    // is reserved in the form permanently rather than toggled with the state. That
    // is what stops the bar shaking at the release point: the reservation used to
    // appear and disappear with the pin, which moved the sentinel that decides the
    // pin, which flipped it back, every frame.
    //
    // Reserved height is the bar plus the --padding-xl gap the frame puts above it.
    // Margins are zeroed inline because the button carries margins that no readable
    // CSS rule accounts for, so raising selector specificity has no effect on them;
    // in both states it is placed by its insets, so it wants none.
    const reserve = () => {
      const height = Math.round(button.getBoundingClientRect().height);
      if (!height) return;
      this.style.setProperty('--atc-height', `${height}px`);
      if (form) form.style.paddingBlockEnd = 'calc(var(--atc-height, 0px) + var(--padding-xl))';
    };

    const setPinned = (pinned) => {
      if (this.dataset.atcPinned === String(pinned)) return;
      this.dataset.atcPinned = String(pinned);
      reserve();
    };

    button.style.margin = '0';
    this.dataset.atcPinned = 'false';
    reserve();

    this.pinObserver = new IntersectionObserver(
      ([entry]) => setPinned(!entry.isIntersecting),
      { threshold: 0 }
    );
    this.pinObserver.observe(sentinel);

    this.resize = new ResizeObserver(() => {
      const height = Math.round(button.getBoundingClientRect().height);
      if (!height || this.style.getPropertyValue('--atc-height') === `${height}px`) return;
      reserve();
    });
    this.resize.observe(button);
  }

  /**
   * Hide the label Evey renders for the date/time field once the field itself has
   * moved out from under it (designer, Pastel #21: "remove 'date/time'").
   *
   * Matched by the same text as the trigger, so the two are retargeted together
   * when Evey's wording changes. Marked with an attribute rather than hidden inline
   * so the block's stylesheet owns the rule.
   */
  hideOrphanLabel() {
    const needle = (this.dataset.triggerText || 'date/time').trim().toLowerCase();
    if (!needle) return;

    for (const label of this.source.querySelectorAll('label')) {
      if (label.contains(this.trigger)) continue;
      if ((label.textContent || '').trim().toLowerCase().includes(needle)) {
        label.setAttribute('data-hidden-evey-orphan-label', '');
      }
    }
  }

  /**
   * Replace Evey's own close glyph in the calendar modal with the glossary close icon
   * (designer, Pastel #25: "use the new icon"). The SVG comes from a <template> the
   * block renders, so nothing is inlined here. Runs from the body observer each time
   * the modal is rebuilt; idempotent via the data attribute.
   */
  swapCloseIcon() {
    const template = this.querySelector('template[data-hidden-evey-close-icon]');
    const close = document.querySelector('#evey-schedule-calendar-modal .modal-window > .close');
    if (!template || !close || close.hasAttribute('data-hidden-icon')) return;

    const icon = template.content.querySelector('svg');
    if (!icon) return;

    close.replaceChildren(icon.cloneNode(true));
    close.setAttribute('data-hidden-icon', '');
  }

  /** @returns {HTMLElement | null} */
  findTrigger() {
    const selector = this.dataset.triggerSelector?.trim();
    if (selector) {
      const el = this.source.querySelector(selector);
      if (el instanceof HTMLElement) return el;
    }

    const needle = (this.dataset.triggerText || 'date/time').trim().toLowerCase();
    if (!needle) return null;

    const candidates = this.source.querySelectorAll('button, a, [role="button"]');
    for (const el of candidates) {
      if (!(el instanceof HTMLElement)) continue;
      if ((el.textContent || '').trim().toLowerCase().includes(needle)) return el;
    }

    return null;
  }
}

if (!customElements.get('hidden-evey-mount')) {
  customElements.define('hidden-evey-mount', HiddenEveyMount);
}
